import React, { useEffect, useMemo, useRef, useState } from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import { startReferralBonusConfetti } from './durationCouponConfetti';
import TivigoToast from './TivigoToast';
import UserPanelTableLoading from './UserPanelTableLoading';
import { formatCreditAmount } from './creditCurrency';
import './userPanelReferrals.css';

const REFERRAL_STATUS_FILTERS = Object.freeze([
  { value: 'all', label: 'Tüm Durumlar' },
  { value: 'eligible', label: 'Bonus Hazır' },
  { value: 'claimed', label: 'Bonus Alındı' },
  { value: 'unqualified', label: 'Şart Sağlanmadı' },
  { value: 'no-package', label: 'Paket Almadı' },
]);

const toBoolean = (value) => value === true || value === 1 || value === '1' || value === 'true';

const getReferralIdentity = (referral, index = 0) => String(
  referral?.id ?? referral?.user_id ?? referral?.referred_user_id ?? referral?.username ?? index,
);

const getMonths = (referral) => Math.max(0, Number(
  referral?.first_purchase_months
    ?? referral?.firstPurchaseMonths
    ?? referral?.packageMonths
    ?? 0,
) || 0);

const getClaimed = (referral) => toBoolean(referral?.bonus_claimed ?? referral?.bonusClaimed);

const getEligible = (referral) => {
  const explicitValue = referral?.bonus_eligible ?? referral?.bonusEligible;
  if (explicitValue !== undefined && explicitValue !== null) return toBoolean(explicitValue);
  return getMonths(referral) >= 6 && !getClaimed(referral);
};

const getBonusAmount = (referral) => Number(
  referral?.bonus_amount ?? referral?.bonusAmount ?? 250,
) || 250;

const maskEmail = (email) => {
  const value = String(email || '');
  if (!value.includes('@')) return value || '-';

  const [localPart, domain] = value.split('@');
  if (localPart.length <= 2) return value;

  return `${localPart.charAt(0)}${'*'.repeat(Math.max(localPart.length - 2, 1))}${localPart.charAt(localPart.length - 1)}@${domain}`;
};

const copyText = async (text) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === 'undefined') throw new Error('Kopyalama desteklenmiyor.');

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();

  const copied = document.execCommand('copy');
  document.body.removeChild(textArea);
  if (!copied) throw new Error('Kopyalama başarısız.');
};

function UserPanelReferrals({
  referrals,
  referralCode,
  loading = false,
  onCopyReferralLink,
  onClaimBonus,
  currency = 'TRY',
  eurTryRate = null,
}) {
  const formatBonus = (amount) => formatCreditAmount(amount, currency, eurTryRate);
  const [localReferrals, setLocalReferrals] = useState(() => (
    Array.isArray(referrals) ? referrals : []
  ));
  const synchronizedReferralsPropRef = useRef(referrals);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterMenuOpen, setIsStatusFilterMenuOpen] = useState(false);
  const [isRowsPerPageMenuOpen, setIsRowsPerPageMenuOpen] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [claimingIds, setClaimingIds] = useState([]);
  const [toast, setToast] = useState(null);
  const bonusConfettiCleanupRef = useRef(null);
  const statusFilterMenuRef = useRef(null);
  const rowsPerPageMenuRef = useRef(null);

  useEffect(() => {
    synchronizedReferralsPropRef.current = referrals;
    setLocalReferrals(Array.isArray(referrals) ? referrals : []);
    setPage(0);
  }, [referrals]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeoutId = window.setTimeout(() => setToast(null), 3600);
    return () => window.clearTimeout(timeoutId);
  }, [toast]);

  useEffect(() => {
    if (!copied) return undefined;
    const timeoutId = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [copied]);

  useEffect(() => () => {
    bonusConfettiCleanupRef.current?.();
    bonusConfettiCleanupRef.current = null;
  }, []);

  useEffect(() => {
    if (!isStatusFilterMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!statusFilterMenuRef.current?.contains(event.target)) {
        setIsStatusFilterMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsStatusFilterMenuOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isStatusFilterMenuOpen]);

  useEffect(() => {
    if (!isRowsPerPageMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rowsPerPageMenuRef.current?.contains(event.target)) {
        setIsRowsPerPageMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsRowsPerPageMenuOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRowsPerPageMenuOpen]);

  const effectiveReferralCode = String(referralCode || '');
  const referralLink = useMemo(() => {
    if (!effectiveReferralCode) return '';
    if (/^https?:\/\//i.test(effectiveReferralCode)) return effectiveReferralCode;

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://tivigo.store';
    return `${origin}/kayit?ref=${encodeURIComponent(effectiveReferralCode)}`;
  }, [effectiveReferralCode]);

  const normalizedReferrals = useMemo(() => {
    const referralsForRender = synchronizedReferralsPropRef.current === referrals
      ? localReferrals
      : (Array.isArray(referrals) ? referrals : []);

    return referralsForRender.map((referral, index) => ({
      id: getReferralIdentity(referral, index),
      raw: referral,
      username: referral?.username || 'Kullanıcı',
      name: referral?.ad || referral?.name || 'İsim belirtilmemiş',
      email: maskEmail(referral?.email),
      searchEmail: String(referral?.email || ''),
      enabled: referral?.enabled === undefined ? true : toBoolean(referral.enabled),
      months: getMonths(referral),
      bonusEligible: getEligible(referral),
      bonusClaimed: getClaimed(referral),
      bonusAmount: getBonusAmount(referral),
    }));
  }, [localReferrals, referrals]);

  const filteredReferrals = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase('tr-TR');

    return normalizedReferrals.filter((referral) => {
      const matchesSearch = !normalizedQuery || [
        referral.username,
        referral.name,
        referral.searchEmail,
      ].some((value) => String(value).toLocaleLowerCase('tr-TR').includes(normalizedQuery));

      let matchesStatus = true;
      if (statusFilter === 'eligible') matchesStatus = referral.bonusEligible && !referral.bonusClaimed;
      if (statusFilter === 'claimed') matchesStatus = referral.bonusClaimed;
      if (statusFilter === 'unqualified') {
        matchesStatus = referral.months > 0 && !referral.bonusEligible && !referral.bonusClaimed;
      }
      if (statusFilter === 'no-package') matchesStatus = referral.months === 0;

      return matchesSearch && matchesStatus;
    });
  }, [normalizedReferrals, searchQuery, statusFilter]);

  const sortedReferrals = useMemo(() => {
    if (!sortConfig.key) return filteredReferrals;

    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const getSortValue = (referral) => {
      if (sortConfig.key === 'months') return referral.months;
      if (sortConfig.key === 'bonus') {
        if (referral.bonusClaimed) return 3;
        if (referral.bonusEligible) return 2;
        return referral.months > 0 ? 1 : 0;
      }
      return referral[sortConfig.key] || '';
    };

    return [...filteredReferrals].sort((firstReferral, secondReferral) => {
      const firstValue = getSortValue(firstReferral);
      const secondValue = getSortValue(secondReferral);

      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        return (firstValue - secondValue) * direction;
      }

      return String(firstValue).localeCompare(String(secondValue), 'tr', {
        numeric: true,
        sensitivity: 'base',
      }) * direction;
    });
  }, [filteredReferrals, sortConfig]);

  const pageCount = Math.max(1, Math.ceil(sortedReferrals.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * rowsPerPage;
  const visibleReferrals = sortedReferrals.slice(pageStart, pageStart + rowsPerPage);
  const displayedFrom = filteredReferrals.length ? pageStart + 1 : 0;
  const displayedTo = Math.min(pageStart + rowsPerPage, filteredReferrals.length);
  const selectedStatusFilter = REFERRAL_STATUS_FILTERS.find((option) => option.value === statusFilter)
    || REFERRAL_STATUS_FILTERS[0];
  const hasActiveFilters = Boolean(searchQuery.trim()) || statusFilter !== 'all';

  useEffect(() => {
    if (page > pageCount - 1) setPage(Math.max(pageCount - 1, 0));
  }, [page, pageCount]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(0);
  };

  const showToast = (message, tone = 'success') => {
    setToast({ message, tone, id: Date.now() });
  };

  const handleCopyReferralLink = async () => {
    if (!referralLink || copying) return;
    setCopied(false);
    setCopying(true);

    try {
      if (onCopyReferralLink) {
        const result = await onCopyReferralLink(referralLink, effectiveReferralCode);
        if (result === false || result?.success === false) {
          throw new Error(result?.message || 'Kopyalama başarısız.');
        }
      } else {
        await copyText(referralLink);
      }
      setCopied(true);
    } catch (error) {
      showToast(error?.message || 'Davet linki kopyalanamadı.', 'error');
    } finally {
      setCopying(false);
    }
  };

  const showUnavailableBonusReason = (referral) => {
    if (referral.months === 0) {
      showToast('Arkadaşınız henüz paket almadığı için bonus beklemede.', 'warning');
      return;
    }

    if (referral.months < 6) {
      showToast('Bonus için arkadaşınızın 6 ay veya üzeri paket alması gerekir.', 'warning');
      return;
    }

    showToast('Bu davetin bonusu henüz kullanıma hazır değil.', 'warning');
  };

  const handleClaimBonus = async (referral) => {
    if (referral.bonusClaimed) return;
    if (!referral.bonusEligible) {
      showUnavailableBonusReason(referral);
      return;
    }
    if (claimingIds.includes(referral.id)) return;

    setClaimingIds((current) => [...current, referral.id]);

    try {
      let result;
      if (!onClaimBonus) throw new Error('Bonus servisi bağlantısı sağlanmadı.');
      const referredUserId = referral.raw?.id
        ?? referral.raw?.user_id
        ?? referral.raw?.referred_user_id
        ?? referral.id;
      result = await onClaimBonus(referredUserId, referral.raw);
      if (result === false || result?.success === false) {
        throw new Error(result?.message || 'Bonus alınamadı.');
      }

      const claimedAmount = Number(
        result?.data?.bonus_amount ?? result?.bonus_amount ?? referral.bonusAmount,
      ) || referral.bonusAmount;

      setLocalReferrals((current) => current.map((item, index) => (
        getReferralIdentity(item, index) === referral.id
          ? {
            ...item,
            bonus_claimed: true,
            bonusClaimed: true,
            bonus_eligible: false,
            bonusEligible: false,
            bonus_amount: claimedAmount,
          }
          : item
      )));
      bonusConfettiCleanupRef.current?.();
      bonusConfettiCleanupRef.current = startReferralBonusConfetti();
      showToast(`${formatBonus(claimedAmount)} bonus bakiyenize eklendi.`);
    } catch (error) {
      showToast(error?.message || 'Bonus alınırken bir hata oluştu.', 'error');
    } finally {
      setClaimingIds((current) => current.filter((id) => id !== referral.id));
    }
  };

  return (
    <div className="tvup-referrals-page">
      <TivigoToast notice={toast} />

      <section className="tvup-referrals-hero" aria-labelledby="tvup-referrals-title">
        <div className="tvup-referrals-hero-copy">
          <h1 id="tvup-referrals-title">Arkadaşlarını davet et, <span>birlikte kazan.</span></h1>
          <p className="tvup-referrals-hero-description">
            Aşağıdaki davet linkinle kayıt olan kullanıcı 6 ay veya üzeri paket aldığında, bakiyene {formatBonus(250)} bonus eklenir.
          </p>
          <div className="tvup-referrals-hero-link">
            <div className="tvup-referrals-link-field">
              <input
                id="tvup-referrals-link-input"
                type="text"
                value={referralLink || 'Referans bağlantısı henüz oluşturulmadı'}
                readOnly
                aria-label="Kişisel referans bağlantısı"
              />
              <button
                type="button"
                onClick={handleCopyReferralLink}
                disabled={!referralLink || copying}
                className={`${copying ? 'is-copying' : ''}${copied ? ' is-copied' : ''}`.trim()}
                aria-label={copied ? 'Davet linki kopyalandı' : 'Davet linkini kopyala'}
                aria-busy={copying}
              >
                {copied ? <CheckCircleRoundedIcon /> : <ContentCopyRoundedIcon />}
                <span aria-live="polite">{copied ? 'Kopyalandı' : 'Kopyala'}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="tvup-referrals-list-panel" aria-labelledby="tvup-referrals-list-title">
        <header className="tvup-referrals-list-toolbar">
          <div>
            <h2 id="tvup-referrals-list-title">Davet Ettiğiniz Kullanıcılar</h2>
          </div>
          <div className="tvup-referrals-list-tools">
            <label className="tvup-referrals-search-field">
              <SearchRoundedIcon />
              <input
                type="text"
                value={searchQuery}
                aria-label="Davet edilen kullanıcılar içinde ara"
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setPage(0);
                }}
              />
            </label>
            <div className="tvup-referrals-status-filter" ref={statusFilterMenuRef}>
              <button
                type="button"
                className="tvup-referrals-status-trigger"
                aria-haspopup="listbox"
                aria-expanded={isStatusFilterMenuOpen}
                onClick={() => {
                  setIsStatusFilterMenuOpen((current) => !current);
                  setIsRowsPerPageMenuOpen(false);
                }}
              >
                <FilterListRoundedIcon />
                <span>{selectedStatusFilter.label}</span>
                <KeyboardArrowDownRoundedIcon />
              </button>
              {isStatusFilterMenuOpen && (
                <div className="tvup-referrals-status-menu" role="listbox" aria-label="Referans bonus durumu">
                  {REFERRAL_STATUS_FILTERS.map((option) => (
                    <button
                      type="button"
                      role="option"
                      aria-selected={statusFilter === option.value}
                      className={statusFilter === option.value ? 'is-selected' : ''}
                      onClick={() => {
                        setStatusFilter(option.value);
                        setPage(0);
                        setIsStatusFilterMenuOpen(false);
                      }}
                      key={option.value}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="tvup-referrals-table-wrap">
          <div className="tvup-referrals-table" role="table" aria-label="Davet edilen kullanıcılar">
            <div className="tvup-referrals-table-head" role="row">
              {[
                ['username', 'Kullanıcı'],
                ['email', 'E-posta'],
                ['months', 'Paket Durumu'],
                ['bonus', 'Bonus'],
              ].map(([key, label]) => {
                const isActive = sortConfig.key === key;
                return (
                  <span
                    role="columnheader"
                    aria-sort={isActive ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                    key={key}
                  >
                    <button
                      type="button"
                      className={`tvup-referrals-sort-button${isActive ? ' is-active' : ''}${isActive && sortConfig.direction === 'asc' ? ' is-ascending' : ''}`}
                      onClick={() => handleSort(key)}
                    >
                      <span>{label}</span>
                      {isActive ? <KeyboardArrowDownRoundedIcon /> : <UnfoldMoreRoundedIcon />}
                    </button>
                  </span>
                );
              })}
            </div>

            {loading ? (
              <UserPanelTableLoading
                rows={1}
                columns={4}
                label="Davetler yükleniyor"
                className="is-compact"
              />
            ) : visibleReferrals.length > 0 ? (
              <div className="tvup-referrals-table-body" role="rowgroup">
                {visibleReferrals.map((referral) => {
                  const isClaiming = claimingIds.includes(referral.id);
                  const hasPackage = referral.months > 0;
                  return (
                    <div className="tvup-referrals-row" role="row" key={referral.id}>
                      <div className="tvup-referrals-user-cell" role="cell" data-label="Kullanıcı">
                        <strong>{referral.username}</strong>
                      </div>

                      <div className="tvup-referrals-email-cell" role="cell" data-label="E-posta">
                        <span>{referral.email}</span>
                      </div>

                      <div className="tvup-referrals-package-cell" role="cell" data-label="Paket Durumu">
                        <span className="tvup-referrals-package-status">
                          <strong>{hasPackage ? `${referral.months} Aylık Paket` : '-'}</strong>
                        </span>
                      </div>

                      <div className="tvup-referrals-bonus-cell" role="cell" data-label="Bonus">
                        {referral.bonusClaimed ? (
                          <span className="tvup-referrals-bonus-claimed">Bonusu Aldınız</span>
                        ) : (
                          <button
                            type="button"
                            className={`tvup-referrals-claim-button${referral.bonusEligible ? '' : ' tvup-referrals-claim-button--waiting'}`}
                            onClick={() => handleClaimBonus(referral)}
                            disabled={isClaiming}
                            aria-label={referral.bonusEligible
                              ? `${referral.username} için ${formatBonus(referral.bonusAmount)} bonus al`
                              : hasPackage
                                ? `${referral.username} için bonus şartı sağlanmadı`
                                : `${referral.username} paket almadı`}
                          >
                            {isClaiming ? <span className="tvup-referrals-button-spinner" /> : null}
                            {isClaiming
                              ? 'İşleniyor'
                              : referral.bonusEligible
                                ? `${formatBonus(referral.bonusAmount)} Bonus Al`
                                : hasPackage
                                  ? 'Şart Sağlanmadı'
                                  : 'Paket Almadı'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : hasActiveFilters ? (
              <div className="tvup-referrals-empty" role="status">
                <span><SearchRoundedIcon /></span>
                <strong>Sonuç bulunamadı</strong>
                <p>Arama metnini veya durum filtresini değiştirin.</p>
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setPage(0);
                  }}
                >
                  Filtreleri Temizle
                </button>
              </div>
            ) : (
              <div className="tvup-referrals-empty" role="status">
                <span><PersonAddAlt1RoundedIcon /></span>
                <strong>Henüz kimseyi davet etmediniz</strong>
                <p>Davet linkinizi paylaşın; kayıt olan kullanıcılar burada görünsün.</p>
                <button type="button" onClick={handleCopyReferralLink} disabled={!referralLink}>
                  <ContentCopyRoundedIcon /> Davet Linkini Kopyala
                </button>
              </div>
            )}
          </div>
        </div>

        {!loading && filteredReferrals.length > 0 && (
          <footer className="tvup-referrals-pagination" aria-label="Referans sayfalama">
            <div className="tvup-referrals-page-size">
              <span>Sayfa başına</span>
              <div className="tvup-referrals-page-size-dropdown" ref={rowsPerPageMenuRef}>
                <button
                  type="button"
                  className="tvup-referrals-page-size-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={isRowsPerPageMenuOpen}
                  onClick={() => {
                    setIsRowsPerPageMenuOpen((current) => !current);
                    setIsStatusFilterMenuOpen(false);
                  }}
                >
                  <span>{rowsPerPage}</span>
                  <KeyboardArrowDownRoundedIcon />
                </button>
                {isRowsPerPageMenuOpen && (
                  <div className="tvup-referrals-page-size-menu" role="listbox" aria-label="Sayfa başına kayıt sayısı">
                    {[5, 10, 20].map((option) => (
                      <button
                        type="button"
                        role="option"
                        aria-selected={rowsPerPage === option}
                        className={rowsPerPage === option ? 'is-selected' : ''}
                        onClick={() => {
                          setRowsPerPage(option);
                          setPage(0);
                          setIsRowsPerPageMenuOpen(false);
                        }}
                        key={option}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <span className="tvup-referrals-page-range">{displayedFrom}–{displayedTo} / {filteredReferrals.length} kayıt</span>
            <div className="tvup-referrals-pagination-controls">
              <button
                type="button"
                aria-label="Önceki sayfa"
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                disabled={safePage === 0}
              >
                <KeyboardArrowLeftRoundedIcon />
              </button>
              <strong>{safePage + 1} / {pageCount}</strong>
              <button
                type="button"
                aria-label="Sonraki sayfa"
                onClick={() => setPage((current) => Math.min(current + 1, pageCount - 1))}
                disabled={safePage >= pageCount - 1}
              >
                <KeyboardArrowRightRoundedIcon />
              </button>
            </div>
          </footer>
        )}
      </section>
    </div>
  );
}

export default UserPanelReferrals;
